/**
 * Reiner Prüf-Kern der Rechte (Story 28.2, AD-43): `resolvePermissions` macht aus einer Rollen-Fassung die wirksamen
 * Rechte, `can` prüft ein Recht, `intersectPermissions` bildet die Schnittmenge (Vorschau „Rolle ∩ Admin“, später
 * „Rechte beim Freigeben ∩ Rechte heute“), `checkRoleGrants` meldet Fehler einer Rollen-Fassung auf Deutsch.
 *
 * Rein: keine Datenbank, keine Uhr, kein Netz – läuft auch im Browser. Grundsatz „Sperre im Zweifel“: ein unbekannter
 * Schlüssel, ein unbekannter Teil-Umfang oder eine fehlende Angabe ergibt **kein** Recht. Kein Rechenkern nach AD-27
 * (kein goldener Fall, kein Eintrag in `kernels.json`).
 */
import { z } from 'zod';
import {
  catalogPermissionKeys,
  isAdminOnlyPermission,
  PERMISSION_AREAS,
  PERMISSION_CATALOG,
  permissionLabel,
  permissionLevels,
  SPECIAL_PERMISSIONS,
  type PermissionArea,
  type PermissionCatalog,
  type PermissionKey,
  type PermissionLevel,
  type SpecialPermissionKey,
} from './catalog.ts';

/** Rechte einer Rolle, wie sie in `platform.role_version` stehen (28.3): Stufe je Bereich, Sonderrechte mit Teil-Umfang. */
export interface RoleGrants {
  areas: Partial<Record<PermissionArea, PermissionLevel>>;
  /** `true` = volles Sonderrecht, Text = nur dieser Teil-Umfang. */
  specials: Partial<Record<SpecialPermissionKey, true | string>>;
}

/** Eine Rollen-Fassung als Eingabe des Kerns. */
export interface RoleVersionInput {
  /** Nur die fest eingebaute Admin-Rolle (setzt allein die DB-Schicht, 28.3). */
  isAdmin: boolean;
  /** Katalog-Version, mit der die Fassung gespeichert wurde (nur Auskunft; aufgelöst wird immer nach `catalog`). */
  catalogVersion: number;
  /** Aus `jsonb` gelesen – der Kern traut der Form nicht und lässt Unbekanntes weg. */
  grants: RoleGrants;
}

/** Wirksame Rechte (eingefroren, serialisierbar). */
export interface EffectivePermissions {
  /** Version des Katalogs, nach dem aufgelöst wurde. */
  readonly catalogVersion: number;
  /** Volle Rechte. */
  readonly keys: readonly PermissionKey[];
  /** Nur Teil-Umfänge (Recht nicht voll): Schlüssel → erlaubte Teile. */
  readonly scopes: Readonly<Partial<Record<PermissionKey, readonly string[]>>>;
  /**
   * Teil-Umfänge, die der Katalog der Auflösung bei den gehaltenen Sonderrechten kennt. `can` prüft einen Teil-Umfang nur
   * dagegen – nie gegen einen anderen Katalog (Zweit-Review #1740).
   */
  readonly knownScopes: Readonly<Partial<Record<PermissionKey, readonly string[]>>>;
}

type RawGrants = { full: Set<PermissionKey>; scoped: Map<PermissionKey, Set<string>> };

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function has(raw: RawGrants, key: PermissionKey): boolean {
  return raw.full.has(key) || (raw.scoped.get(key)?.size ?? 0) > 0;
}

/** Grants ohne Abhängigkeiten auflösen; Unbekanntes, Nur-Admin und falsche Typen fallen weg. */
function collect(grants: unknown, catalog: PermissionCatalog): RawGrants {
  const raw: RawGrants = { full: new Set(), scoped: new Map() };
  const g = record(grants);
  const areas = record(g.areas);
  const specials = record(g.specials);
  for (const area of catalog.areas) {
    const level = Object.hasOwn(areas, area.key) ? areas[area.key] : undefined;
    if (level === 'read' || level === 'write') raw.full.add(`${area.key}:read`);
    if (level === 'write') raw.full.add(`${area.key}:write`);
  }
  for (const special of catalog.specials) {
    if (isAdminOnlyPermission(special.key)) continue;
    const value = Object.hasOwn(specials, special.key) ? specials[special.key] : undefined;
    if (value === true) raw.full.add(special.key);
    else if (typeof value === 'string' && Object.hasOwn(special.scopes, value)) raw.scoped.set(special.key, new Set([value]));
  }
  for (const always of catalog.always) raw.full.add(always.key);
  return raw;
}

/** Abhängigkeiten durchsetzen (bis nichts mehr wegfällt): ein Sonderrecht ohne seine Voraussetzung wirkt nicht. */
function enforceRequirements(raw: RawGrants, catalog: PermissionCatalog): void {
  let changed = true;
  while (changed) {
    changed = false;
    for (const special of catalog.specials) {
      if (!has(raw, special.key)) continue;
      if (special.requires.every((req) => raw.full.has(req))) continue;
      raw.full.delete(special.key);
      raw.scoped.delete(special.key);
      changed = true;
    }
  }
}

function freeze(
  catalogVersion: number,
  full: Iterable<PermissionKey>,
  scoped: Map<PermissionKey, Set<string>>,
  order: readonly PermissionKey[],
  known: ReadonlyMap<PermissionKey, ReadonlySet<string>>,
): EffectivePermissions {
  const fullSet = new Set(full);
  const keys = Object.freeze(order.filter((k) => fullSet.has(k)));
  const scopes: Partial<Record<PermissionKey, readonly string[]>> = {};
  const knownScopes: Partial<Record<PermissionKey, readonly string[]>> = {};
  for (const key of order) {
    const knownParts = known.get(key);
    const parts = [...(scoped.get(key) ?? [])].filter((p) => knownParts?.has(p) === true);
    if (!fullSet.has(key) && parts.length > 0) scopes[key] = Object.freeze(parts.sort());
    if ((fullSet.has(key) || parts.length > 0) && knownParts && knownParts.size > 0) knownScopes[key] = Object.freeze([...knownParts].sort());
  }
  return Object.freeze({ catalogVersion, keys, scopes: Object.freeze(scopes), knownScopes: Object.freeze(knownScopes) });
}

/** Teil-Umfänge je Sonderrecht laut Katalog. */
function catalogScopes(catalog: PermissionCatalog): Map<PermissionKey, ReadonlySet<string>> {
  return new Map(catalog.specials.map((s) => [s.key as PermissionKey, new Set(Object.keys(s.scopes))]));
}

/** Teil-Umfänge, die eine wirksame Rechte-Menge für einen Schlüssel kennt (eigene Eigenschaften, Sperre im Zweifel). */
function knownOf(e: EffectivePermissions, key: PermissionKey): ReadonlySet<string> {
  const known = e.knownScopes as Readonly<Record<string, unknown>>;
  const parts = Object.hasOwn(known, key) ? known[key] : undefined;
  return new Set(Array.isArray(parts) ? (parts as string[]) : []);
}

/**
 * Wirksame Rechte einer Rollen-Fassung nach dem **aktuellen** Katalog: Admin bekommt jeden Schlüssel (samt Teil-
 * Umfängen), jede andere Rolle nur, was ihre Grants ausdrücklich nennen – nie ein Nur-Admin-Recht. `time:own` hat jede
 * Rolle. Eine ältere `catalogVersion` ändert nichts: Schlüssel, die der Katalog nicht mehr kennt, fallen weg.
 */
export function resolvePermissions(roleVersion: RoleVersionInput, catalog: PermissionCatalog = PERMISSION_CATALOG): EffectivePermissions {
  const order = catalogPermissionKeys(catalog);
  const known = catalogScopes(catalog);
  if (roleVersion.isAdmin === true) return freeze(catalog.version, order, new Map(), order, known);
  const raw = collect(roleVersion.grants, catalog);
  enforceRequirements(raw, catalog);
  return freeze(catalog.version, raw.full, raw.scoped, order, known);
}

/**
 * Hat die Person dieses Recht? Ohne `scope` nur das volle Recht; mit `scope` muss der Teil-Umfang im Katalog der
 * Auflösung (`effective.knownScopes`) bei diesem Sonderrecht stehen, dann reicht das volle Recht oder genau dieser Teil-Umfang. Unbekannter Schlüssel oder
 * Teil-Umfang → `false`.
 */
export function can(effective: EffectivePermissions, key: string, scope?: string): boolean {
  if (scope === undefined) return (effective.keys as readonly string[]).includes(key);
  if (!knownOf(effective, key as PermissionKey).has(scope)) return false;
  if ((effective.keys as readonly string[]).includes(key)) return true;
  const scopes = effective.scopes as Readonly<Record<string, unknown>>;
  const parts = Object.hasOwn(scopes, key) ? scopes[key] : undefined;
  return Array.isArray(parts) && parts.includes(scope);
}

/**
 * Schnittmenge zweier wirksamer Rechte (nie mehr als jede Seite): volles Recht nur, wenn beide es voll haben; sonst die
 * gemeinsamen Teil-Umfänge. Weichen die Katalog-Versionen ab, gilt die kleinere (Sperre im Zweifel).
 */
export function intersectPermissions(a: EffectivePermissions, b: EffectivePermissions): EffectivePermissions {
  const order = [...a.keys, ...(Object.keys(a.scopes) as PermissionKey[])];
  const parts = (e: EffectivePermissions, key: PermissionKey): Set<string> | 'full' | null => {
    if (e.keys.includes(key)) return 'full';
    const s = e.scopes[key];
    return s ? new Set(s) : null;
  };
  const full: PermissionKey[] = [];
  const scoped = new Map<PermissionKey, Set<string>>();
  for (const key of order) {
    const pa = parts(a, key);
    const pb = parts(b, key);
    if (pa === null || pb === null) continue;
    if (pa === 'full' && pb === 'full') full.push(key);
    else if (pa === 'full') scoped.set(key, pb as Set<string>);
    else if (pb === 'full') scoped.set(key, pa);
    else scoped.set(key, new Set([...pa].filter((x) => pb.has(x))));
  }
  // Nur Teil-Umfänge, die beide Seiten kennen (Sperre im Zweifel).
  const known = new Map<PermissionKey, ReadonlySet<string>>();
  for (const key of order) {
    const kb = knownOf(b, key);
    const both = [...knownOf(a, key)].filter((x) => kb.has(x));
    if (both.length > 0) known.set(key, new Set(both));
  }
  return freeze(Math.min(a.catalogVersion, b.catalogVersion), full, scoped, order, known);
}

/** Ein Befund von `checkRoleGrants`. */
export interface RoleGrantsFinding {
  code: 'invalid' | 'unknownArea' | 'unknownLevel' | 'unknownSpecial' | 'unknownScope' | 'adminOnly' | 'missingRequirement';
  /** Betroffener Schlüssel (Bereich oder Sonderrecht), wenn es einen gibt. */
  key: string | null;
  /** Deutscher Satz für die Oberfläche (28.9) und die 422 des Commands (28.3). */
  message: string;
}

/**
 * Prüft eine Rollen-Fassung gegen den Katalog und meldet jeden Fehler als deutschen Satz: unbekannter Bereich, unbekannte
 * Stufe, unbekanntes Sonderrecht oder Teil-Umfang, Nur-Admin-Recht (nie vergebbar) und fehlende Abhängigkeit. Leere
 * Liste = in Ordnung.
 */
export function checkRoleGrants(grants: unknown, catalog: PermissionCatalog = PERMISSION_CATALOG): RoleGrantsFinding[] {
  const findings: RoleGrantsFinding[] = [];
  if (grants === null || typeof grants !== 'object' || Array.isArray(grants)) {
    return [{ code: 'invalid', key: null, message: 'Die Rechte der Rolle sind ungültig.' }];
  }
  const g = record(grants);
  for (const k of Object.keys(g)) {
    if (k !== 'areas' && k !== 'specials') findings.push({ code: 'invalid', key: k, message: `Unbekannte Angabe „${k}“ in den Rechten der Rolle.` });
  }
  const isObject = (v: unknown) => v !== null && typeof v === 'object' && !Array.isArray(v);
  if (!isObject(g.areas)) findings.push({ code: 'invalid', key: 'areas', message: 'Die Stufen der Bereiche fehlen oder sind ungültig.' });
  if (!isObject(g.specials)) findings.push({ code: 'invalid', key: 'specials', message: 'Die Sonderrechte fehlen oder sind ungültig.' });
  const areas = record(g.areas);
  const specials = record(g.specials);
  for (const [key, level] of Object.entries(areas)) {
    if (level === undefined) continue;
    if (!catalog.areas.some((a) => a.key === key)) {
      findings.push({ code: 'unknownArea', key, message: `Unbekannter Bereich „${key}“.` });
    } else if (!(permissionLevels as readonly unknown[]).includes(level)) {
      findings.push({ code: 'unknownLevel', key, message: `Unbekannte Stufe „${String(level)}“ im Bereich „${permissionLabelOfArea(key, catalog)}“.` });
    }
  }
  for (const [key, value] of Object.entries(specials)) {
    if (value === undefined) continue;
    if (isAdminOnlyPermission(key)) {
      findings.push({ code: 'adminOnly', key, message: `„${permissionLabel(key, catalog) ?? key}“ hat nur der Admin; das Recht lässt sich nicht vergeben.` });
      continue;
    }
    const special = catalog.specials.find((s) => s.key === key);
    if (!special) {
      findings.push({ code: 'unknownSpecial', key, message: `Unbekanntes Sonderrecht „${key}“.` });
    } else if (value !== true && !(typeof value === 'string' && Object.hasOwn(special.scopes, value))) {
      findings.push({ code: 'unknownScope', key, message: `Unbekannter Umfang „${String(value)}“ beim Sonderrecht „${special.name}“.` });
    }
  }
  const raw = collect(grants, catalog);
  for (const special of catalog.specials) {
    if (!has(raw, special.key)) continue;
    for (const req of special.requires) {
      if (raw.full.has(req)) continue;
      findings.push({
        code: 'missingRequirement',
        key: special.key,
        message: `„${special.name}“ wirkt nur zusammen mit „${permissionLabel(req, catalog) ?? req}“.`,
      });
    }
  }
  return findings;
}

function permissionLabelOfArea(key: string, catalog: PermissionCatalog): string {
  return catalog.areas.find((a) => a.key === key)?.name ?? key;
}

/** Deutsche Sätze der Form-Prüfung (Zweit-Review #1740: keine englischen Zod-Standardmeldungen). */
export const ROLE_GRANTS_SCHEMA_TEXTS = {
  shape: 'Die Rechte der Rolle sind unvollständig oder enthalten eine unbekannte Angabe. Bitte die Seite neu laden.',
  areas: 'Unbekannter Bereich in den Rechten der Rolle. Bitte die Seite neu laden.',
  level: 'Unbekannte Stufe. Erlaubt sind „Kein Zugriff“, „Sehen“ und „Bearbeiten“.',
  specials: 'Unbekanntes Sonderrecht in den Rechten der Rolle. Bitte die Seite neu laden.',
  special: 'Ungültiger Wert bei einem Sonderrecht: erlaubt sind „an“ oder ein bekannter Teil-Umfang.',
} as const;

function specialGrantSchema(scopes: readonly string[]): z.ZodType<true | string | undefined> {
  const [first, ...rest] = scopes;
  const error = ROLE_GRANTS_SCHEMA_TEXTS.special;
  return (first === undefined ? z.literal(true, { error }) : z.union([z.literal(true), z.enum([first, ...rest])], { error })).optional();
}

/** Strenges Schema der Grants (Command `updateRolePermissions`, 28.3): Unbekanntes und Nur-Admin-Rechte lehnt es ab. */
export const roleGrantsSchema: z.ZodType<RoleGrants> = z
  .strictObject(
    {
      areas: z.strictObject(
        Object.fromEntries(PERMISSION_AREAS.map((a) => [a.key, z.enum(permissionLevels, { error: ROLE_GRANTS_SCHEMA_TEXTS.level }).optional()])),
        { error: ROLE_GRANTS_SCHEMA_TEXTS.areas },
      ),
      specials: z.strictObject(
        Object.fromEntries(SPECIAL_PERMISSIONS.map((s) => [s.key, specialGrantSchema(Object.keys(s.scopes))])),
        { error: ROLE_GRANTS_SCHEMA_TEXTS.specials },
      ),
    },
    { error: ROLE_GRANTS_SCHEMA_TEXTS.shape },
  )
  .superRefine((value, ctx) => {
    for (const finding of checkRoleGrants(value)) ctx.addIssue({ code: 'custom', message: finding.message, path: finding.key ? [finding.key] : [] });
  }) as unknown as z.ZodType<RoleGrants>;

/**
 * Anforderung einer Route (`x-permission`) oder eines Commands (`permission`, Story 28.4): ein Recht, ein Recht mit
 * Teil-Umfang (`data:bulk#tasks` = volles Recht oder genau dieser Teil-Umfang) oder eine Liste, von der **jedes**
 * Element erfüllt sein muss.
 */
export type PermissionRequirement = PermissionKey | `${SpecialPermissionKey}#${string}` | readonly (PermissionKey | `${SpecialPermissionKey}#${string}`)[];

export interface PermissionRequirementPart {
  key: PermissionKey;
  scope?: string;
}

/**
 * Zerlegt eine Anforderung; `null`, wenn ein Teil unbekannt ist (Schlüssel, Teil-Umfang, leere Liste, falscher Typ) –
 * Sperre im Zweifel.
 */
export function parsePermissionRequirement(requirement: unknown, catalog: PermissionCatalog = PERMISSION_CATALOG): PermissionRequirementPart[] | null {
  const items: unknown[] = Array.isArray(requirement) ? requirement : [requirement];
  if (items.length === 0) return null;
  const keys = new Set<string>(catalogPermissionKeys(catalog));
  const parts: PermissionRequirementPart[] = [];
  for (const item of items) {
    if (typeof item !== 'string') return null;
    const [key, scope, rest] = item.split('#');
    if (rest !== undefined || key === undefined || !keys.has(key)) return null;
    if (scope !== undefined) {
      const special = catalog.specials.find((s) => s.key === key);
      if (!special || !Object.hasOwn(special.scopes, scope)) return null;
      parts.push({ key: key as PermissionKey, scope });
    } else parts.push({ key: key as PermissionKey });
  }
  return parts;
}

/** Erfüllen die Rechte die Anforderung? Unbekannte Anforderung → `false`. */
export function canRequirement(effective: EffectivePermissions, requirement: unknown): boolean {
  const parts = parsePermissionRequirement(requirement);
  return parts !== null && parts.every((p) => can(effective, p.key, p.scope));
}
